import type { AnalyticsReportSummary } from '../../types/analytics';

export class AnalyticsPollTimeoutError extends Error {
  constructor() {
    super('analytics report polling timed out');
    this.name = 'AnalyticsPollTimeoutError';
  }
}
type FetchStatus = (id: string) => Promise<AnalyticsReportSummary>;

interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  now?: () => number;
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>;
  onStatus?: (report: AnalyticsReportSummary) => void;
}

const terminalStatuses = new Set(['completed', 'partial', 'failed', 'cancelled']);

function abortError(): Error {
  const error = new Error('aborted');
  error.name = 'AbortError';
  return error;
}

function defaultWait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(abortError()); return; }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// Sequential polling: the next delay starts only after the previous request
// settles, so a slow API can never create overlapping status requests.
export async function pollAnalyticsReport(
  reportId: string,
  fetchStatus: FetchStatus,
  options: PollOptions = {},
): Promise<AnalyticsReportSummary> {
  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 6 * 60 * 1000;
  const now = options.now ?? Date.now;
  const wait = options.wait ?? defaultWait;
  const startedAt = now();

  while (true) {
    if (options.signal?.aborted) throw abortError();
    if (now() - startedAt >= timeoutMs) throw new AnalyticsPollTimeoutError();
    try {
      const report = await fetchStatus(reportId);
      options.onStatus?.(report);
      if (terminalStatuses.has(report.status)) return report;
    } catch (error) {
      if ((error as Error).name === 'AbortError') throw error;
      // A transient network error does not reset the total timeout budget.
    }
    const remaining = timeoutMs - (now() - startedAt);
    if (remaining <= 0) throw new AnalyticsPollTimeoutError();
    await wait(Math.min(intervalMs, remaining), options.signal);
  }
}
